/**
 * Design System Tabs Component
 *
 * Wraps MUI Tabs with design system token integration.
 * Provides consistent tab styling across the application.
 *
 * Features:
 * - Design system color scheme (primary, secondary, etc.)
 * - Multiple variants (standard, scrollable, fullWidth)
 * - Proper accessibility attributes
 * - Custom sx prop support
 * - Responsive behavior
 *
 * Usage:
 * <DSTabs value={activeTab} onChange={handleTabChange}>
 *   <DSTab label="Overview" />
 *   <DSTab label="Details" />
 *   <DSTab label="History" />
 * </DSTabs>
 *
 * With custom styling:
 * <DSTabs
 *   value={activeTab}
 *   onChange={handleTabChange}
 *   variant="fullWidth"
 *   sx={{ borderTop: '2px solid red' }}
 * >
 *   <DSTab label="Tab 1" />
 *   <DSTab label="Tab 2" />
 * </DSTabs>
 */

import React from 'react';
import { Tabs } from '@mui/material';
import PropTypes from 'prop-types';
import { useResponsive } from '../../hooks';

/**
 * DSTabs component - Design System Tabs wrapper
 *
 * @component
 * @param {number} value - Currently selected tab index
 * @param {function} onChange - Callback when tab changes: (event, newValue) => void
 * @param {string} variant - Tab variant: 'standard' | 'scrollable' | 'fullWidth'
 * @param {string} color - Indicator color: 'primary' | 'secondary' | 'inherit'
 * @param {object} sx - Additional MUI sx prop for custom styling
 * @param {boolean} centered - Center the tabs
 * @param {React.ReactNode} children - Tab components
 * @param {...rest} rest - Any other MUI Tabs props
 *
 * @returns {React.ReactElement} Tabs component with design system styling
 */
const DSTabs = React.forwardRef(
  (
    {
      value,
      onChange,
      variant = 'standard',
      color = 'primary',
      sx = {},
      centered = false,
      scrollButtons = 'auto',
      glass = false,
      children,
      ...rest
    },
    ref
  ) => {
    const { isMobile } = useResponsive();

    // Auto-switch to scrollable variant on mobile if not explicitly set
    const finalVariant = isMobile && variant === 'standard' ? 'scrollable' : variant;
    const finalCentered = isMobile ? false : centered; // Disable centering on mobile for scrollable tabs

    // Design system tab styling
    const finalSx = {
      // Base tab styling
      borderBottom: glass ? 'none' : '1px solid var(--neutral-200)',
      minHeight: '48px',
      ...(glass && {
        backgroundColor: 'var(--glass-tab-bg)',
        backdropFilter: 'blur(var(--glass-tab-blur))',
        WebkitBackdropFilter: 'blur(var(--glass-tab-blur))',
        border: '1px solid var(--glass-tab-border)',
        borderRadius: 'var(--radius-lg)',
        padding: '0 var(--space-2)',
        boxShadow: 'var(--glass-shadow-sm)',
      }),

      // Indicator styling
      '& .MuiTabs-indicator': {
        backgroundColor: glass ? 'var(--insight-amber)' : (color === 'secondary' ? 'var(--navy-600)' : 'var(--teal-600)'),
        height: glass ? '4px' : '3px',
        ...(glass && {
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 0 12px rgba(232, 146, 58, 0.35)',
        }),
        transition: 'all var(--duration-fast) var(--ease-out)',
      },

      // Tab text styling
      '& .MuiTab-root': {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '14px',
        color: 'var(--neutral-600)',
        transition: 'color var(--duration-fast) var(--ease-out), background-color var(--duration-fast) var(--ease-out)',
        minWidth: variant === 'scrollable' ? 'auto' : undefined,

        '&:hover': {
          color: color === 'secondary' ? 'var(--navy-600)' : 'var(--teal-600)',
          backgroundColor: color === 'secondary' ? 'var(--navy-50)' : 'var(--teal-50)',
        },

        '&.Mui-selected': {
          color: color === 'secondary' ? 'var(--navy-600)' : 'var(--teal-600)',
          fontWeight: 700,
        },
      },

      // Responsive adjustments
      '@media (max-width: 576px)': {
        '& .MuiTab-root': {
          minWidth: 'auto',
          flex: 1,
          fontSize: '13px',
        },
      },

      // Merge custom sx (custom overrides take precedence)
      ...sx,
    };

    return (
      <Tabs
        ref={ref}
        value={value}
        onChange={onChange}
        variant={finalVariant}
        centered={finalCentered}
        scrollButtons={scrollButtons}
        textColor="inherit"
        indicatorColor="primary"
        aria-label="design-system-tabs"
        sx={finalSx}
        {...rest}
      >
        {children}
      </Tabs>
    );
  }
);

// Display name for debugging
DSTabs.displayName = 'DSTabs';

// PropTypes for type checking
DSTabs.propTypes = {
  /**
   * The index of the currently selected tab
   */
  value: PropTypes.number.isRequired,

  /**
   * Callback fired when the tab value changes
   */
  onChange: PropTypes.func.isRequired,

  /**
   * The tab variant to use
   */
  variant: PropTypes.oneOf(['standard', 'scrollable', 'fullWidth']),

  /**
   * The color scheme for the indicator
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'inherit']),

  /**
   * Additional MUI sx prop for custom styling
   */
  sx: PropTypes.object,

  /**
   * If true, center the tabs
   */
  centered: PropTypes.bool,

  /**
   * Tab components to render
   */
  children: PropTypes.node.isRequired,

  /**
   * Scroll buttons visibility for scrollable variant
   */
  scrollButtons: PropTypes.oneOf(['auto', 'desktop', 'on', 'off']),

  /**
   * If true, apply glassmorphic styling to the tab bar
   */
  glass: PropTypes.bool,

  /**
   * Aria label for accessibility
   */
  'aria-label': PropTypes.string,
};

// Default props

export default DSTabs;
